import React from "react";
import PropTypes from "prop-types";
import withWidth from "@material-ui/core/withWidth";
import {
  withPropsStyles,
  styledBy,
} from "../withPropsStyles/withPropsStyles";
import {
  primaryColor,
  warningColor,
  dangerColor,
  successColor,
  infoColor,
  roseColor,
  grayColor,
  whiteColor,
} from "../styles";

function Ribbon({ ...props }) {
  const { classes, variant, children } = props;

  return (
    <div className={classes.ribbon + " " + classes[variant]}>
      <span className={classes.content + " " + classes[variant + "Content"]}>
        {children}
      </span>
    </div>
  );
}

Ribbon.defaultProps = {
  variant: "corner",
  color: "primary",
  size: "md",
};

Ribbon.propTypes = {
  classes: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(["corner", "top", "right", "left"]),
  color: PropTypes.oneOf([
    "primary",
    "warning",
    "danger",
    "success",
    "info",
    "rose",
    "gray",
  ]),
  size: PropTypes.oneOf(["sm", "md", "lg", "bp"]),
  children: PropTypes.node.isRequired,
};

const styles = (props, theme) => {
  let newProps = Object.assign({}, props);
  if (newProps.size === "bp") {
    newProps.size =
      newProps.width === "sm" || newProps.width === "xs"
        ? "sm"
        : newProps.width === "md"
        ? "md"
        : "lg";
  }
  return {
    ribbon: {
      backgroundColor: styledBy("color", {
        primary: primaryColor[0],
        warning: warningColor[0],
        danger: dangerColor[0],
        success: successColor[0],
        info: infoColor[0],
        rose: roseColor[0],
        gray: grayColor[0],
      })(newProps),
      position: "absolute",
      zIndex: 2,
    },
    content: {
      backgroundColor: styledBy("color", {
        primary: primaryColor[0],
        warning: warningColor[0],
        danger: dangerColor[0],
        success: successColor[0],
        info: infoColor[0],
        rose: roseColor[0],
        gray: grayColor[0],
      })(newProps),
      ...theme.typography.body1,
      color: whiteColor,
      display: "block",
    },
    corner: {
      backgroundColor: "transparent",
      textAlign: "right",
      right: "-5px",
      top: "-5px",
      overflow: "hidden",
      width: styledBy("size", {
        sm: 50,
        md: 75,
        lg: 100,
      })(newProps),
      height: styledBy("size", {
        sm: 50,
        md: 75,
        lg: 100,
      })(newProps),
    },
    cornerContent: {
      textAlign: "center",
      transform: "rotate(45deg)",
      boxShadow: "0 3px 10px -5px rgba(0, 0, 0, 1)",
      position: "absolute",
      top: styledBy("size", {
        sm: 13,
        md: 19,
        lg: 16,
      })(newProps),
      right: styledBy("size", {
        sm: -17,
        md: -21,
        lg: -28,
      })(newProps),
      width: styledBy("size", {
        sm: 75,
        md: 100,
        lg: 119,
      })(newProps),
      lineHeight: styledBy("size", {
        sm: "15px",
        md: "20px",
        lg: "30px",
      })(newProps),
      fontSize: styledBy("size", {
        sm: "0.75rem",
        md: "0.875rem",
        lg: "1rem",
      })(newProps),
      "&::before": {
        content: "''",
        position: "absolute",
        left: "0px",
        top: "100%",
        zIndex: 1,
        borderLeft: "3px solid #999",
        borderRight: "3px solid transparent",
        borderBottom: "3px solid transparent",
        borderTop: "3px solid #999",
      },
      "&::after": {
        content: "''",
        position: "absolute",
        right: "0px",
        top: "100%",
        zIndex: 1,
        borderLeft: "3px solid transparent",
        borderRight: "3px solid #999",
        borderBottom: "3px solid transparent",
        borderTop: "3px solid #999",
      },
    },
    top: {
      backgroundColor: styledBy("color", {
        primary: primaryColor[4],
        warning: warningColor[4],
        danger: dangerColor[4],
        success: successColor[4],
        info: infoColor[4],
        rose: roseColor[4],
        gray: grayColor[4],
      })(newProps),
      display: "inline-block",
      textDecoration: "none",
      padding: "0 9px 4.5px",
      textAlign: "center",
      top: 0,
      right: theme.spacing(1),
      boxSizing: "border-box",
      width: styledBy("size", {
        sm: 100,
        md: 120,
        lg: 150,
      })(newProps),
      "&::after": {
        content: "''",
        display: "block",
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderColor: styledBy("color", {
          primary: primaryColor[4] + " transparent transparent transparent",
          warning: warningColor[4] + " transparent transparent transparent",
          danger: dangerColor[4] + " transparent transparent transparent",
          success: successColor[4] + " transparent transparent transparent",
          info: infoColor[4] + " transparent transparent transparent",
          rose: roseColor[4] + " transparent transparent transparent",
          gray: grayColor[4] + " transparent transparent transparent",
        })(newProps),
        position: "absolute",
        left: 0,
        top: "100%",
        boxSizing: "border-box",
        borderWidth: styledBy("size", {
          sm: "40.5px 50px 0 50px",
          md: "40.5px 60px 0 60px",
          lg: "40.5px 75px 0 75px",
        })(newProps),
      },
    },
    topContent: {
      backgroundColor: "transparent",
      borderLeft: "1px solid " + whiteColor,
      borderRight: "1px solid " + whiteColor,
      position: "relative",
      height: "100%",
      padding: "30px 12px 24px",
      boxSizing: "border-box",
      fontSize: styledBy("size", {
        sm: "0.875rem",
        md: "1rem",
        lg: "1.25rem",
      })(newProps),
      "&::before, &::after": {
        content: "''",
        display: "block",
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderColor: styledBy("color", {
          primary: primaryColor[4] + " transparent transparent transparent",
          warning: warningColor[4] + " transparent transparent transparent",
          danger: dangerColor[4] + " transparent transparent transparent",
          success: successColor[4] + " transparent transparent transparent",
          info: infoColor[4] + " transparent transparent transparent",
          rose: roseColor[4] + " transparent transparent transparent",
          gray: grayColor[4] + " transparent transparent transparent",
        })(newProps),
        borderWidth: styledBy("size", {
          sm: "36px 41px 0 41px",
          md: "36px 51px 0 51px",
          lg: "36px 66px 0 66px",
        })(newProps),
        marginTop: "-1px",
        position: "absolute",
        top: "100%",
        left: "-1px",
        zIndex: 5,
        boxSizing: "border-box",
      },
      "&::before": {
        borderColor: whiteColor + " transparent transparent transparent",
        marginTop: 0,
      },
    },
    right: {
      padding: 0,
      margin: 0,
      boxSizing: "border-box",
      top: 2,
      right: styledBy("size", {
        sm: -15,
        md: -12,
        lg: -10,
      })(newProps),
      backgroundColor: "transparent",
      "&::before": {
        content: "''",
        height: 0,
        width: 0,
        display: "block",
        position: "absolute",
        top: styledBy("size", {
          sm: 1,
          md: 2,
          lg: 3,
        })(newProps),
        left: styledBy("size", {
          sm: 15,
          md: 5,
          lg: 0,
        })(newProps),
        borderStyle: "solid",
        borderWidth: styledBy("size", {
          sm: 11,
          md: 23,
          lg: 29,
        })(newProps),
        borderTopColor: "rgba(0, 0, 0, .3)",
        borderBottomColor: "rgba(0, 0, 0, .3)",
        borderRightColor: "rgba(0, 0, 0, .3)",
        borderLeftColor: "transparent",
      },
      "&::after": {
        content: "''",
        background: "rgba(0, 0, 0, .3)",
        display: "block",
        position: "absolute",
        height: styledBy("size", {
          sm: 1,
          md: 2,
          lg: 3,
        })(newProps),
        bottom: styledBy("size", {
          sm: -1,
          md: -2,
          lg: -3,
        })(newProps),
        left: styledBy("size", {
          sm: 37,
          md: 51,
          lg: 59,
        })(newProps),
        right: 3,
      },
    },
    rightContent: {
      display: "block",
      position: "relative",
      overflow: "visible",
      fontSize: styledBy("size", {
        sm: "0.75rem",
        md: "0.875rem",
        lg: "1rem",
      })(newProps),
      lineHeight: styledBy("size", {
        sm: "0.75rem",
        md: "0.875rem",
        lg: "1rem",
      })(newProps),
      height: 10,
      padding: styledBy("size", {
        sm: 6,
        md: 18,
        lg: 28,
      })(newProps),
      marginLeft: 29,
      textDecoration: "none",
      "&::after": {
        content: "''",
        height: 0,
        width: 0,
        display: "block",
        position: "absolute",
        bottom: -15,
        right: 0,
        borderTop: styledBy("color", {
          primary: "15px solid " + primaryColor[4],
          warning: "15px solid " + warningColor[4],
          danger: "15px solid " + dangerColor[4],
          success: "15px solid " + successColor[4],
          info: "15px solid " + infoColor[4],
          rose: "15px solid " + roseColor[4],
          gray: "15px solid " + grayColor[4],
        })(newProps),
        borderRight: "15px solid transparent",
      },
      "&::before": {
        content: "''",
        height: 0,
        width: 0,
        display: "block",
        position: "absolute",
        top: 0,
        left: styledBy("size", {
          sm: -11,
          md: -23,
          lg: -29,
        })(newProps),
        borderStyle: "solid",
        borderWidth: styledBy("size", {
          sm: 11,
          md: 23,
          lg: 29,
        })(newProps),
        borderTopColor: styledBy("color", {
          primary: primaryColor[0],
          warning: warningColor[0],
          danger: dangerColor[0],
          success: successColor[0],
          info: infoColor[0],
          rose: roseColor[0],
          gray: grayColor[0],
        })(newProps),
        borderBottomColor: styledBy("color", {
          primary: primaryColor[0],
          warning: warningColor[0],
          danger: dangerColor[0],
          success: successColor[0],
          info: infoColor[0],
          rose: roseColor[0],
          gray: grayColor[0],
        })(newProps),
        borderRightColor: "transparent",
        borderLeftColor: "transparent",
      },
    },
    left: {
      padding: 0,
      margin: 0,
      boxSizing: "border-box",
      top: 2,
      left: styledBy("size", {
        sm: -15,
        md: -12,
        lg: -10,
      })(newProps),
      backgroundColor: "transparent",
      "&::after": {
        content: "''",
        height: 0,
        width: 0,
        display: "block",
        position: "absolute",
        top: styledBy("size", {
          sm: 1,
          md: 2,
          lg: 3,
        })(newProps),
        right: styledBy("size", {
          sm: 15,
          md: 5,
          lg: 0,
        })(newProps),
        borderStyle: "solid",
        borderWidth: styledBy("size", {
          sm: 11,
          md: 23,
          lg: 29,
        })(newProps),
        borderTopColor: "rgba(0, 0, 0, .3)",
        borderBottomColor: "rgba(0, 0, 0, .3)",
        borderLeftColor: "rgba(0, 0, 0, .3)",
        borderRightColor: "transparent",
      },
      "&::before": {
        content: "''",
        background: "rgba(0, 0, 0, .3)",
        display: "block",
        position: "absolute",
        height: styledBy("size", {
          sm: 1,
          md: 2,
          lg: 3,
        })(newProps),
        bottom: styledBy("size", {
          sm: -1,
          md: -2,
          lg: -3,
        })(newProps),
        right: styledBy("size", {
          sm: 37,
          md: 51,
          lg: 59,
        })(newProps),
        left: 3,
      },
    },
    leftContent: {
      display: "block",
      position: "relative",
      overflow: "visible",
      zIndex: 1,
      fontSize: styledBy("size", {
        sm: "0.75rem",
        md: "0.875rem",
        lg: "1rem",
      })(newProps),
      lineHeight: styledBy("size", {
        sm: "0.75rem",
        md: "0.875rem",
        lg: "1rem",
      })(newProps),
      height: 10,
      padding: styledBy("size", {
        sm: 6,
        md: 18,
        lg: 28,
      })(newProps),
      marginRight: 29,
      textDecoration: "none",
      "&::before": {
        content: "''",
        height: 0,
        width: 0,
        display: "block",
        position: "absolute",
        bottom: -15,
        left: 0,
        borderTop: styledBy("color", {
          primary: "15px solid " + primaryColor[4],
          warning: "15px solid " + warningColor[4],
          danger: "15px solid " + dangerColor[4],
          success: "15px solid " + successColor[4],
          info: "15px solid " + infoColor[4],
          rose: "15px solid " + roseColor[4],
          gray: "15px solid " + grayColor[4],
        })(newProps),
        borderLeft: "15px solid transparent",
      },
      "&::after": {
        content: "''",
        height: 0,
        width: 0,
        display: "block",
        position: "absolute",
        top: 0,
        right: styledBy("size", {
          sm: -11,
          md: -23,
          lg: -29,
        })(newProps),
        borderStyle: "solid",
        borderWidth: styledBy("size", {
          sm: 11,
          md: 23,
          lg: 29,
        })(newProps),
        borderTopColor: styledBy("color", {
          primary: primaryColor[0],
          warning: warningColor[0],
          danger: dangerColor[0],
          success: successColor[0],
          info: infoColor[0],
          rose: roseColor[0],
          gray: grayColor[0],
        })(newProps),
        borderBottomColor: styledBy("color", {
          primary: primaryColor[0],
          warning: warningColor[0],
          danger: dangerColor[0],
          success: successColor[0],
          info: infoColor[0],
          rose: roseColor[0],
          gray: grayColor[0],
        })(newProps),
        borderLeftColor: "transparent",
        borderRightColor: "transparent",
      },
    },
  };
};

export default withWidth()(withPropsStyles(styles)(Ribbon));
